<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 style="grid-area: header" id="sca15-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="sca-test-grid"
      >
        <p>
          <a
            target="_blank"
            href="https://www.vcgs.org.au/tests/spinocerebellar-ataxia-15/"
            >Spinocerebellar ataxia (SCA)
          </a>
          is a progressive, degenerative, genetic disease with multiple types,
          each of which could be considered a disease in its own right.
        </p>

        <v-row>
          <v-col class="custom-col">
            <v-alert
              text
              outlined
              type="warning"
              icon="mdi-cash-usd-outline"
              class="mr-1 pb-0 pl-0 mt-0"
            >
              <p>
                Patient to be referred to a neurologist for diagnostic tests and
                to a genetics clinic for predictive testing.
              </p>
            </v-alert>
          </v-col>
        </v-row>

        <v-checkbox
          v-model="previousTest"
          item-text="key"
          item-value="value"
          class="pt-3 pb-3"
          label="A family member was previously tested"
          :id="testId + '.familyPrevTested'"
        >
        </v-checkbox>
        <v-checkbox
          v-model="previouslyTested"
          v-if="previousTest === true"
          item-text="key"
          item-value="value"
          label="They were tested at VCGS"
          class="pb-3"
          :id="testId + '.previouslyTestedVcgs'"
        >
        </v-checkbox>
        <SendEmailAlert v-if="previouslyTested !== true && previousTest" />
        <v-row v-if="previouslyTested === true && previousTest === true">
          <v-col class="custom-col">
            <span class="text-title">VCGS Sample ID</span>
            <v-text-field
              v-model="posCtrlSampleId"
              :id="testId + '.posCtrlSampleId'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint"
              >e.g. 20C100123. This is located in the top right section of our
              reports. If you cannot find this please provide the name and date
              of birth of the person previously tested.</span
            >
          </v-col>
        </v-row>
        <v-row v-if="previouslyTested === true && previousTest === true">
          <v-col class="custom-col">
            <span class="text-title">Relationship to person being tested</span>
            <v-text-field
              v-model="rhipToPosCtrl"
              :id="testId + '.rhipToPosCtrl'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint">e.g. sibling, parent, aunt, etc.</span>
          </v-col>
        </v-row>
        <v-row v-if="previouslyTested === true && previousTest === true">
          <v-col class="custom-col">
            <span class="text-title"
              >Is the person being tested affected by the same condition?</span
            >
            <v-text-field
              v-model="relationAffected"
              :id="testId + '.relationAffected'"
              outlined
              hide-details="auto"
            ></v-text-field>
            <span class="text-hint">e.g. mildly affected</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="custom-col">
            <span class="text-title">Clinical notes</span>
            <v-textarea
              v-model="clinicalNotes"
              :id="testId + '.clinicalNotes'"
              outlined
              hide-details="auto"
            ></v-textarea>
            <span class="text-hint"
              >e.g. clinical indication for testing, relevant family history,
              results of relevant prior genetic and non-genetic investigations,
              biochemistry, histopathology and imaging, etc.</span
            >
          </v-col>
        </v-row>

        <TestPriceAlert :medicare="medicare" :price="price" />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        id="sca_next_section"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FormRulesMixin from "../../mixins/FormRulesMixin";
import DropDownMixin from "../../mixins/DropDownMixin";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceAlert from "../common/TestPriceAlert";
import SendEmailAlert from "../common/SendEmailAlert.vue";
import { EventBus } from "@/event-bus";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";

export default {
  name: "",
  mixins: [FormRulesMixin, DropDownMixin, RetrieveJSONMixin],
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
  },
  components: { TestPriceAlert, SendEmailAlert },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
  },

  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.simpleTestPricing = await this.execRetrieveJson(
      "json/pricing/simple.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    const element = document.getElementById("sca-title");
    EventBus.$emit("getPatientData");

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    EventBus.$emit("medicareSCA15Status", true);
  },

  data: () => ({
    urgent: false,
    urgentReason: "",
    simpleTestPricing: null,
    previousTest: null,
    previouslyTested: null,
    relationAffected: "",
    rhipToPosCtrl: "",
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    posCtrlSampleId: "",
    billTo: "MEDICARE",
    clinicalNotes: "",
  }),

  computed: {
    getPriceDetails() {
      if (!this.simpleTestPricing) return null;
      return this.simpleTestPricing[this.testId];
    },
    price() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.price;
      } else {
        return null;
      }
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.testCode;
      } else {
        return "";
      }
    },
    medicare() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails !== null) {
        return priceDetails.medicare;
      } else {
        return null;
      }
    },
  },

  methods: {
    transferData: function() {
      return {
        urgent: this.urgent,
        urgency: this.urgency,
        urgentReason: "",
        testType: this.testId,
        familyPrevTested: this.previouslyTested,
        posCtrlSampleId: this.posCtrlSampleId,
        relationAffected: this.relationAffected,
        clinicalNotes: this.clinicalNotes,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: true,
        rhipToPosCtrl: this.rhipToPosCtrl,
        billTo: this.billTo,
      };
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
  },
};
</script>
<style scoped></style>
